import {
  metricKeys,
  type ExpValue,
  type GameEvent,
  type GameStateSnapshot,
  type MetricKey,
  type ReadingSource,
  type ResourceValue,
  type ValidatedObservation,
} from '@/domain/types'
import type { GamePreset } from '@/presets/types'

const emptyResource: ResourceValue = { current: null, max: null, ratio: null }

export class GameStateEngine {
  private state: GameStateSnapshot | null = null
  private pendingLevelIncrease: {
    from: number
    to: number
    capturedAt: number
    confidence: number
    expAbsolute: number | null
    expRatio: number | null
    expRequired: number | null
  } | null = null
  private pendingExpWrap: {
    capturedAt: number
    confidence: number
    previousAbsolute: number | null
    previousRequired: number | null
  } | null = null
  private mesosDiscontinuous = false
  private readonly discontinuousMetrics = new Set<MetricKey>()
  private readonly totals = Object.fromEntries(metricKeys.map((key) => [key, 0])) as Record<MetricKey, number>
  private readonly accepted = Object.fromEntries(metricKeys.map((key) => [key, 0])) as Record<MetricKey, number>
  private readonly lastSampleAt = Object.fromEntries(metricKeys.map((key) => [key, null])) as Record<MetricKey, number | null>

  constructor(private readonly preset: GamePreset) {}

  apply(sessionId: string, observation: ValidatedObservation): { state: GameStateSnapshot; events: GameEvent[] } {
    const previous = this.state
    if (observation.mesos.availability === 'unavailable') this.mesosDiscontinuous = true
    for (const metric of ['hp', 'mp', 'mesos', 'mapName'] as const) {
      if (observation[metric].availability === 'unavailable') this.discontinuousMetrics.add(metric)
    }
    for (const key of metricKeys) {
      if (this.preset.capabilities[key] === 'unavailable') continue
      const sample = observation[key]
      if (sample.availability === 'unavailable') continue
      const sampleAt = sample.capturedAt
      if (this.lastSampleAt[key] === sampleAt) continue
      this.lastSampleAt[key] = sampleAt
      if (this.totals[key] === 0 && !observation.accepted[key]) continue
      this.totals[key] += 1
      if (observation.accepted[key]) this.accepted[key] += 1
    }

    const level = observation.accepted.level ? observation.level.value : (previous?.level ?? null)
    const exp = observation.accepted.exp
      ? this.resolveExp(observation.exp.value!, level, previous?.level === level ? previous.exp : undefined)
      : previous?.level === level
        ? previous.exp
        : { absolute: null, ratio: null }

    const state: GameStateSnapshot = {
      capturedAt: observation.capturedAt,
      observedAt: Object.fromEntries(metricKeys.map((key) => [
        key,
        observation.accepted[key] ? observation[key].capturedAt : (previous?.observedAt?.[key] ?? null),
      ])) as Record<MetricKey, number | null>,
      level,
      exp,
      hp: observation.accepted.hp ? this.mergeResource(previous?.hp, observation.hp.value!, observation.hp.source) : (previous?.hp ?? emptyResource),
      mp: observation.accepted.mp ? this.mergeResource(previous?.mp, observation.mp.value!, observation.mp.source) : (previous?.mp ?? emptyResource),
      mesos: observation.accepted.mesos ? observation.mesos.value : (previous?.mesos ?? null),
      mapName: observation.accepted.mapName ? observation.mapName.value : (previous?.mapName ?? null),
      jobName: observation.accepted.jobName ? observation.jobName.value : (previous?.jobName ?? null),
      confidence: Object.fromEntries(metricKeys.map((key) => [key, observation.accepted[key] ? observation[key].confidence : (previous?.confidence[key] ?? 0)])) as Record<MetricKey, number>,
      coverage: Object.fromEntries(metricKeys.map((key) => [key, this.totals[key] ? this.accepted[key] / this.totals[key] : 0])) as Record<MetricKey, number>,
    }
    // A confirmed baseline reset must neither consume old level-up evidence nor
    // leave its downward jump available to a later level reading.
    if (observation.expRebaseline) {
      this.pendingLevelIncrease = null
      this.pendingExpWrap = null
    }
    const events = previous ? this.createEvents(sessionId, previous, state) : []
    if (observation.expRebaseline) {
      this.pendingLevelIncrease = null
      this.pendingExpWrap = null
    }
    this.state = state
    for (const metric of ['hp', 'mp', 'mesos', 'mapName'] as const) {
      if (observation.accepted[metric]) this.discontinuousMetrics.delete(metric)
    }
    if (observation.accepted.mesos) this.mesosDiscontinuous = false
    return { state, events }
  }

  markMetricsDiscontinuous(metrics: readonly MetricKey[]): void {
    for (const metric of metrics) this.discontinuousMetrics.add(metric)
    if (metrics.includes('mesos')) this.mesosDiscontinuous = true
  }

  resetBaseline(state: GameStateSnapshot | null = null): void {
    this.state = state
    this.pendingLevelIncrease = null
    this.pendingExpWrap = null
    this.mesosDiscontinuous = false
    this.discontinuousMetrics.clear()
  }

  private createEvents(sessionId: string, previous: GameStateSnapshot, current: GameStateSnapshot): GameEvent[] {
    const events: GameEvent[] = []
    const push = (event: Omit<GameEvent, 'id' | 'sessionId' | 'capturedAt'>) => events.push({
      ...event,
      id: crypto.randomUUID(),
      sessionId,
      capturedAt: current.capturedAt,
    })

    const levelIncreased = previous.level !== null && current.level === previous.level + 1
    if (levelIncreased) {
      this.pendingLevelIncrease = {
        from: previous.level!,
        to: current.level!,
        capturedAt: current.capturedAt,
        confidence: current.confidence.level,
        expAbsolute: previous.exp.absolute,
        expRatio: previous.exp.ratio,
        expRequired: previous.exp.requiredExp ?? null,
      }
    }
    const previousExpAt = previous.observedAt?.exp ?? previous.capturedAt
    const currentExpAt = current.observedAt?.exp ?? current.capturedAt
    const expObservationAdvanced = previousExpAt !== null
      && currentExpAt !== null
      && currentExpAt > previousExpAt
    const ratioReset = previous.exp.ratio !== null
      && current.exp.ratio !== null
      && current.exp.ratio < previous.exp.ratio - 0.018
    const absoluteReset = previous.exp.absolute !== null
      && current.exp.absolute !== null
      && current.exp.absolute < previous.exp.absolute
    const highToLowWrap = previous.exp.ratio !== null
      && current.exp.ratio !== null
      && previous.exp.ratio >= 0.85
      && current.exp.ratio <= 0.2
    const levelSupportsReset = this.pendingLevelIncrease !== null
      && current.level === this.pendingLevelIncrease.to
    const pendingLevelReset = levelSupportsReset
      && expObservationAdvanced
      && ((this.pendingLevelIncrease!.expRatio !== null
          && current.exp.ratio !== null
          && current.exp.ratio < this.pendingLevelIncrease!.expRatio - 0.018)
        || (this.pendingLevelIncrease!.expAbsolute !== null
          && current.exp.absolute !== null
          && current.exp.absolute < this.pendingLevelIncrease!.expAbsolute))
    const wrapped = highToLowWrap
      || pendingLevelReset
      || (levelSupportsReset && expObservationAdvanced && (ratioReset || absoluteReset))
    if (wrapped) {
      this.pendingExpWrap = {
        capturedAt: current.capturedAt,
        confidence: current.confidence.exp,
        previousAbsolute: previous.exp.absolute ?? this.pendingLevelIncrease?.expAbsolute ?? null,
        previousRequired: previous.exp.requiredExp ?? this.pendingLevelIncrease?.expRequired ?? null,
      }
    }
    if (this.pendingLevelIncrease && current.capturedAt - this.pendingLevelIncrease.capturedAt > 15_000) this.pendingLevelIncrease = null
    if (this.pendingExpWrap && current.capturedAt - this.pendingExpWrap.capturedAt > 15_000) this.pendingExpWrap = null
    if (this.pendingLevelIncrease && this.pendingExpWrap) {
      const required = this.pendingExpWrap.previousRequired ?? this.pendingLevelIncrease.expRequired
      const previousAbsolute = this.pendingExpWrap.previousAbsolute ?? this.pendingLevelIncrease.expAbsolute
      if (required !== null && previousAbsolute !== null && previousAbsolute <= required) {
        push({
          type: 'level-up',
          from: this.pendingLevelIncrease.from,
          to: this.pendingLevelIncrease.to,
          confidence: Math.min(this.pendingLevelIncrease.confidence, this.pendingExpWrap.confidence),
        })
        this.pendingLevelIncrease = null
        this.pendingExpWrap = null
      }
    }

    if (!this.discontinuousMetrics.has('hp') && this.observationsAreContiguous('hp', previous, current)) {
      this.resourceEvent('hp', previous.hp, current.hp, current.confidence.hp, push)
    }
    if (!this.discontinuousMetrics.has('mp') && this.observationsAreContiguous('mp', previous, current)) {
      this.resourceEvent('mp', previous.mp, current.mp, current.confidence.mp, push)
    }
    if (!this.mesosDiscontinuous && !this.discontinuousMetrics.has('mesos') && previous.mesos !== null && current.mesos !== null && previous.mesos !== current.mesos) {
      push({ type: 'mesos-changed', amount: current.mesos - previous.mesos, from: previous.mesos, to: current.mesos, confidence: current.confidence.mesos })
    }
    if (!this.discontinuousMetrics.has('mapName') && previous.mapName !== null && current.mapName !== null && previous.mapName !== current.mapName) {
      push({ type: 'map-changed', previousMapName: previous.mapName, mapName: current.mapName, confidence: current.confidence.mapName })
    }
    return events
  }

  private resourceEvent(
    metric: 'hp' | 'mp',
    previous: ResourceValue,
    current: ResourceValue,
    confidence: number,
    push: (event: Omit<GameEvent, 'id' | 'sessionId' | 'capturedAt'>) => void,
  ): void {
    if (previous.current === null || current.current === null) return
    if (previous.max !== null && current.max !== null && previous.max !== current.max) return
    const before = previous.current
    const after = current.current
    if (before === after) return
    push({
      type: after < before ? `${metric}-consumed` : `${metric}-recovered`,
      amount: Math.abs(after - before),
      from: before,
      to: after,
      confidence,
    })
  }

  private observationsAreContiguous(metric: 'hp' | 'mp', previous: GameStateSnapshot, current: GameStateSnapshot): boolean {
    const before = previous.observedAt?.[metric] ?? previous.capturedAt
    const after = current.observedAt?.[metric] ?? current.capturedAt
    return before !== null && after !== null && after > before && after - before <= 2_000
  }

  private mergeResource(previous: ResourceValue | undefined, current: ResourceValue, source: ReadingSource): ResourceValue {
    if (source === 'bar') {
      return { current: null, max: null, ratio: current.ratio ?? previous?.ratio ?? null }
    }
    return {
      current: current.current ?? previous?.current ?? null,
      max: current.max ?? previous?.max ?? null,
      ratio: current.ratio ?? previous?.ratio ?? null,
    }
  }

  private resolveExp(value: ExpValue, level: number | null, previous?: ExpValue): ExpValue {
    if (value.absolute !== null) {
      const required = value.requiredExp ?? previous?.requiredExp
      return required === null || required === undefined
        ? value
        : {
            ...value,
            requiredExp: required,
            requiredExpApproximate: value.requiredExpApproximate ?? previous?.requiredExpApproximate ?? true,
          }
    }
    if (value.ratio === null || level === null) return value
    const required = value.requiredExp ?? previous?.requiredExp ?? null
    if (required === null || required <= 0) return value
    return {
      ...value,
      absolute: Math.round(required * value.ratio),
      estimated: true,
      requiredExp: required,
      requiredExpApproximate: value.requiredExpApproximate ?? previous?.requiredExpApproximate ?? true,
    }
  }
}
